import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Journals } from '../../../../types/Journals.types';
import {
  MasterlistCheckStatus,
  MasterlistJournalCheck,
} from '../../../../types/masterlistCheck.types';
import { masterlistIssues, statusLabel } from './masterlistCheck';

// Same palettes as the error / warning / success alerts. Check ISSNs is red like its table chip;
// confirm match and not found are softer, since the gap may be ours. The status text carries the
// meaning, the colour only backs it up.
const TONES = {
  error: {
    backgroundColor: 'var(--color-light-grey-red)',
    borderColor: 'var(--color-grey-red)',
  },
  warning: {
    backgroundColor: 'var(--color-very-pale-orange)',
    borderColor: 'var(--color-dark-greyish-blue)',
  },
  success: {
    backgroundColor: 'var(--color-light-greyish-lime-green)',
    borderColor: 'var(--color-scifree-teal)',
  },
};
const TONE_BY_STATUS: Record<MasterlistCheckStatus, keyof typeof TONES> = {
  matches: 'success',
  check_issns: 'error',
  confirm_match: 'warning',
  not_found: 'warning',
};

/** Collapsible masterlist status and issue lines for the journal modal. */
export function MasterlistCheckSection({
  check,
  journal,
}: {
  check: MasterlistJournalCheck;
  journal: Journals;
}) {
  const { t } = useTranslation();
  const issues = masterlistIssues(check, journal, t);
  // A ceased journal is a warning even when it matches.
  const tone =
    check.status === 'matches' && check.flags.includes('ceased')
      ? TONES.warning
      : TONES[TONE_BY_STATUS[check.status]];

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: tone.borderColor,
        backgroundColor: tone.backgroundColor,
        borderRadius: '6px',
        color: 'var(--color-deep-ocean-blue)',
      }}
    >
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          boxShadow: 'none',
          border: 'none',
          backgroundColor: 'transparent',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          id="journal-masterlist-check-header"
          aria-controls="journal-masterlist-check-content"
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': { mr: 8 },
            '& .MuiAccordionSummary-content': { margin: 0 },
          }}
        >
          <Typography variant="body1">
            <Box component="span" fontWeight={700}>
              {t('agreements.masterlistCheck.summaryTitle')}:
            </Box>{' '}
            {statusLabel(check, t)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 24, pb: 16 }}>
          {issues.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 20 }}>
              {issues.map((issue, i) => (
                <Typography key={i} component="li" variant="body2">
                  {issue}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2">
              {t('agreements.masterlistCheck.noIssues')}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
